import React, { useRef, useState } from 'react';
import type { ColumnInstance, HeaderGroup, TableInstance } from 'react-table';
import { Icon } from '../Icon';
import {
  intl,
  intlFmt,
  useLayerDeferedVisibility,
  usePersistentPreferences
} from '../../../lib';
import type { CustomColumn, PersistentColumnState, TableProps } from '../Table';
import { DEFAULT_PLACEMENT_CONFIG } from '../../../util';
import { Tool } from '../Tool';
import { Checkbox } from '../../form';
import type { LanguageProperty } from '../../generated/LanguageProperty';

/**
 * Props for a {@link TableHeader} component.
 */
export type TableHeaderProps = Pick<
  TableProps<never>,
  'hideableColumns' | 'paging'
> &
  Pick<TableInstance<any>, 'headerGroups' | 'allColumns'> & {
    /** Enable client-side sorting for all columns */
    sortable?: boolean;
    /** If an action column should be present in the header. */
    hasActions?: boolean;
    /**If sorting is enabled this aria-label is placed on the icon for descending*/
    ariaLabelDescending?: LanguageProperty;
    /**If sorting is enabled this aria-label is placed on the icon for ascending*/
    ariaLabelAscending?: LanguageProperty;
  };

/**
 * Custom {@link ColumnInstance} type which includes the custom options added
 * for our table columns.
 */
type CustomColumnInstance = ColumnInstance<any> &
  Pick<CustomColumn<never>, 'disableHideColumn' | 'stateId' | 'persistState'>;

/**
 * {@link TableHeader} column hider checkbox component.
 * This inner component is needed because it uses some hooks internally to fully support all its necessary features.
 *
 * @param column the {@link CustomColumnInstance}
 * @param columnIndex column index
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
function ColumnHiderCheckbox({
  column
}: {
  column: CustomColumnInstance;
}): React.JSX.Element {
  const [
    colPreferencesState,
    persistState
  ] = usePersistentPreferences<PersistentColumnState>(column.stateId);
  const toggleHiddenProps = column.getToggleHiddenProps();
  return (
    <Checkbox
      debounceWait={0}
      // the Header should normally only be a regular string
      label={column.Header as string}
      value={toggleHiddenProps.checked}
      onChange={value => {
        // the click handler of the column tries to get the value from the event target
        // but since our Checkbox can have multiple different targets for usability, that wouldn't work like that anyway
        // therefore, just passing the value in a format that the react-table can read
        toggleHiddenProps.onChange({
          target: {
            checked: value
          }
        });

        // try to persist the table state if it should be persisted
        if ((column.persistState?.indexOf('hidden') ?? -1) >= 0) {
          persistState({
            ...colPreferencesState,
            hidden: !value
          });
        }
      }}
    />
  );
}

/**
 * The header of a Table component.
 *
 * @param properties {@link TableHeaderProps}
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function TableHeader({
  headerGroups,
  sortable = false,
  hasActions = false,
  allColumns,
  hideableColumns = false,
  paging,
  ariaLabelAscending = 'common.sortdown',
  ariaLabelDescending = 'common.sortup'
}: TableHeaderProps): React.JSX.Element {
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const { layerProps, triggerProps, renderLayer } = useLayerDeferedVisibility({
    isOpen: isOpen,
    ...DEFAULT_PLACEMENT_CONFIG,
    placement: 'bottom-end',
    ResizeObserver: ResizeObserver,
    container: menuContainerRef?.current ?? undefined,
    overflowContainer: paging !== 'infinite-scroll',
    onOutsideClick: () => setOpen(prev => !prev)
  });
  // hook config for the column hider menu
  const layer = renderLayer(
    <div
      className="ehs-menu"
      {...layerProps}
      style={{ ...layerProps.style }}
      ref={layerProps.ref}
    >
      {allColumns
        .filter((column: CustomColumnInstance) => !column.disableHideColumn)
        .map((column: CustomColumnInstance, columnIndex: number) => (
          <ColumnHiderCheckbox
            key={`hide-checkbox-${columnIndex}`}
            column={column}
          />
        ))}
    </div>
  );

  const addAriaSort = (column: any) => {
    if (column.canSort && column.isSorted) {
      if (column.isSortedDesc) {
        return 'descending';
      } else return 'ascending';
    } else return 'none';
  };

  function TableHeaderBuilder({
    column,
    isSortable,
    columnIndex
  }: {
    column: any;
    isSortable: boolean | undefined;
    columnIndex: number;
  }) {
    return (
      <th
        {...column.getHeaderProps(isSortable && column.getSortByToggleProps())}
        title={undefined}
        aria-sort={addAriaSort(column)}
        className={isSortable ? 'header-sort' : undefined}
        style={{
          ...column.getHeaderProps().style,
          width: column.width
        }}
        key={`table-column-${columnIndex}`}
      >
        <div
          className="header-wrapper"
          role={column.canSort ? 'button' : undefined}
          tabIndex={column.canSort ? 0 : -1}
          onKeyPress={event => {
            if (
              column.canSort &&
              (event.key === 'Enter' || event.key === ' ')
            ) {
              column.toggleSortBy();
            }
          }}
          title={
            column.canSort
              ? intlFmt('common.sortby.specific', column.Header)
              : undefined
          }
        >
          {column.render('Header')}
          {column.isSorted && (
            <Icon
              aria-label={
                column.isSortedDesc
                  ? intl(ariaLabelDescending)
                  : intl(ariaLabelAscending)
              }
              iconName={column.isSortedDesc ? 'down' : 'up'}
            />
          )}
        </div>
      </th>
    );
  }

  return (
    <thead>
      {hideableColumns && isOpen && layer}
      {headerGroups.map((headerGroup: HeaderGroup, headerIndex: number) => (
        <tr
          className="header"
          {...headerGroup.getHeaderGroupProps()}
          key={`table-header-${headerIndex}`}
        >
          {headerGroup.headers.map((column: any, columnIndex: number) => (
            <TableHeaderBuilder
              key={columnIndex}
              column={column}
              isSortable={sortable && !column.disableSortBy}
              columnIndex={columnIndex}
            />
          ))}
          {hasActions && (
            <th className="action-head">{intl('common.actions')}</th>
          )}
          {hideableColumns && (
            <th className="column-hider-head">
              <Tool
                {...triggerProps}
                className="column-dropdown-tool"
                icon="menu"
                onClick={isOpen ? () => setOpen(false) : () => setOpen(true)}
                tooltip={intl('view.button.customize')}
                aria-expanded={isOpen}
              />
              {/* This div is here to help with positioning the menu properly.
              Otherwise using the `container` property won't work quite as expected. */}
              <div
                className="column-hider-menu-container"
                ref={menuContainerRef}
              />
            </th>
          )}
        </tr>
      ))}
    </thead>
  );
}
